// The link-in-bio builder's step machine and page-data shape, as pure
// functions.
//
// WHY THIS IS NOT INSIDE BioEditorPage
// ────────────────────────────────────
// It used to be, inline in a 1,400-line component behind an API-fetching
// mount. Two real bugs lived here and both were found by tracing a field
// through the component by hand rather than by anything that would catch
// them a second time:
//
//   1. The edit path copied fields one by one and never included
//      `layout_type`, so opening any existing page for edit silently reset
//      its layout to the default on the next save.
//   2. The default was `layout_type: ""`, which matched NEITHER the grid
//      branch nor the links branch, so the content step rendered neither
//      section. Survivable while layout was chosen first; not afterwards.
//
// Pulled out here they are directly testable, and the regressions are
// mutation-verifiable — see __tests__/bioPageState.test.js.

/**
 * The builder's two steps.
 *
 * Frozen and referenced by symbol everywhere — `STEPS.THEME`, never the bare
 * string "theme". A stale literal is how the Back button kept pointing at a
 * step that had been renamed away: it set an unreachable state and blanked
 * the dialog, and nothing caught it because a string is a string.
 *
 * This is JavaScript, so there is no compile-time union. What replaces it:
 *
 *   • a typo'd SYMBOL (`STEPS.THEEME`) is `undefined`, and `assertStep`
 *     throws immediately with the offending value;
 *   • a bare LITERAL passed to the setter is caught by a source scan in
 *     __tests__/bioPageState.test.js, which fails the build in CI.
 *
 * A real union needs TypeScript. These two together close the same hole.
 */
export const STEPS = Object.freeze({
  THEME: "theme",
  CONTENT: "content",
});

export const STEP_VALUES = Object.freeze(Object.values(STEPS));

/** Throws on anything that is not a known step. Call it in the setter. */
export function assertStep(step) {
  if (!STEP_VALUES.includes(step)) {
    throw new Error(
      `Unknown builder step ${JSON.stringify(step)}. ` +
      `Use STEPS.THEME or STEPS.CONTENT — not a bare string. ` +
      `Known: ${STEP_VALUES.join(", ")}`
    );
  }
  return step;
}

/** Layouts the content step can render. `mixed` shows both sections. */
export const LAYOUTS = ["classic", "instagram", "mixed"];

/** A new page's layout. NOT "" — see note 2 above. Mixed is the honest
 *  default: it shows every section, so nothing a creator adds is invisible. */
export const DEFAULT_LAYOUT = "mixed";

export const DEFAULT_EMAIL_CAPTURE = {
  enabled: false,
  heading: "Stay in the loop",
  subtext: "Get updates straight to your inbox",
  cta_text: "Subscribe",
  success_message: "You're in!",
};

/** Fields carried between the creation steps and a saved document. Keeping
 *  this list in ONE place is what stops a field being dropped from the edit
 *  path the way `layout_type` was. */
export const PAGE_FIELDS = [
  "display_name", "username", "category", "bio", "profile_image", "slug",
  "social_links", "grid_items", "button_links", "embed_blocks",
  "email_capture", "style", "layout_type",
];

/** Blank page data for a brand-new page. A fresh object every call. */
export function emptyPageData() {
  return {
    layout_type: DEFAULT_LAYOUT,
    display_name: "",
    username: "",
    category: "",
    bio: "",
    profile_image: "",
    slug: "",
    social_links: {},
    grid_items: [],
    button_links: [],
    embed_blocks: [],
    email_capture: { ...DEFAULT_EMAIL_CAPTURE },
    // No style until a theme is picked. Deliberately null rather than a
    // default preset: the renderer shows an explicit "not set up yet" state
    // instead of guessing, and guessing is how the old default silently
    // replaced a creator's choice.
    style: null,
  };
}

/** Page data for editing an EXISTING page.
 *
 *  Built by iterating PAGE_FIELDS rather than by hand-copying, so adding a
 *  field to the shape cannot leave the edit path behind. That hand-copying
 *  is exactly how `layout_type` went missing.
 */
export function pageDataFromExisting(page) {
  const base = emptyPageData();
  const out = { ...base };
  PAGE_FIELDS.forEach((key) => {
    const v = page?.[key];
    if (v !== undefined && v !== null && v !== "") out[key] = v;
  });
  out.style = page?.style ? { ...page.style } : null;
  out.layout_type = page?.layout_type || DEFAULT_LAYOUT;
  // Shop config round-trips so reopening a shop page shows the
  // selection that is live, not an empty one. Same omission that
  // silently reset layout_type for every edit.
  out.shop = page?.shop || null;
  return out;
}

/** Apply a chosen theme. Picking a theme IS the action that advances the
 *  flow, so this also settles the layout if one was never set. */
export function withTheme(prev, preset) {
  return {
    ...prev,
    style: { ...preset.style, preset_id: preset.id },
    layout_type: prev?.layout_type || DEFAULT_LAYOUT,
  };
}

/** Which step should the builder open on?
 *
 *  Editing skips the theme step — the theme is already chosen, and the
 *  Change affordance in the content header covers wanting to switch.
 */
export function initialStep(existingPage) {
  return existingPage ? STEPS.CONTENT : STEPS.THEME;
}

/** Does this layout show the image grid / the links list?
 *
 *  Aug 2026 — `shop` was PRODUCTS ONLY, on the reasoning that a layout
 *  wanting products AND links AND photos is Mix gaining products rather
 *  than Shop quietly growing a second collection.
 *
 *  Reversed (P, 2026-08-21): a creator who picks Shop must still be able
 *  to add links and posts, exactly as in Mix. What made the old rule feel
 *  wrong is that the LAYOUT was deciding what exists, when the CONTENT
 *  already decides it — every section in the renderer is behind a
 *  `.length > 0`, so a shop page with no links shows no links section
 *  without the layout needing an opinion. "Only shop if that is all you
 *  added" falls out of that for free.
 *
 *  `classic` and `instagram` stay narrow. Those two are deliberate
 *  single-collection layouts and nobody asked for them to change. */
export function showsGrid(layoutType) {
  return layoutType === "instagram" || layoutType === "mixed"
    || layoutType === "shop";
}
export function showsLinks(layoutType) {
  return layoutType === "classic" || layoutType === "mixed"
    || layoutType === "shop";
}

/** Only the shop layout carries products. */
export function showsShop(layoutType) {
  return layoutType === "shop";
}

/**
 * Required-field gating for publish.
 *
 * Display name + a free, well-formed slug. Nothing else — an empty page
 * with a theme is a valid page, and gating publish on content people have
 * not written yet is how creators abandon halfway.
 */
export function requiredFieldErrors(data, slugStatus = {}) {
  const errs = {};
  if (!data?.display_name?.trim()) errs.display_name = "Display name is required";

  const slug = (data?.slug || "").trim();
  if (!slug) errs.slug = "URL slug is required";
  else if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/.test(slug))
    errs.slug = "Only lowercase letters, numbers, hyphens";
  else if (slugStatus.available === false)
    errs.slug = slugStatus.reason || "Slug unavailable";

  return errs;
}

export function canPublish(data, slugStatus = {}) {
  return Object.keys(requiredFieldErrors(data, slugStatus)).length === 0;
}


/**
 * The page as the API expects it.
 *
 * THE THIRD SHAPE. `PAGE_FIELDS` guards the editor <-> document shapes, but
 * the request body is different again: the server resolves the whole look
 * from a top-level `preset_id` and ignores any style a client sends. Posting
 * `pageData` verbatim therefore sent a nested `style` and NO `preset_id`, so
 * Pydantic fell back to its default — "quiet" — and every page was created
 * as the first theme no matter which one the creator picked.
 *
 * Building the body here, with a test asserting the chosen theme survives,
 * is what stops that happening a second time.
 */
export function toApiPayload(data) {
  return {
    display_name: data.display_name,
    username: data.username,
    category: data.category,
    bio: data.bio,
    profile_image: data.profile_image,
    slug: data.slug,
    social_links: data.social_links,
    grid_items: data.grid_items,
    button_links: data.button_links,
    embed_blocks: data.embed_blocks,
    email_capture: data.email_capture,
    layout_type: data.layout_type,
    // The ONLY visual input the server accepts. Everything else about the
    // look is resolved from it server-side.
    preset_id: data.style?.preset_id,
  };
}

/**
 * Which layout does a theme selection imply?
 *
 * Layout is no longer a separate control: the card a creator clicks already
 * shows a specific layout, so clicking "Loud & Clear" in the Images group
 * means grid. Offering a layout picker afterwards asked the same question
 * twice and let the answer disagree with the card they chose.
 */
export function layoutFromSelection(layoutType) {
  return LAYOUTS.includes(layoutType) ? layoutType : DEFAULT_LAYOUT;
}

/**
 * The page object the EDITOR PREVIEW renders.
 *
 * ═══════════════════════════════════════════════════════════════════
 *  WHY THIS FUNCTION EXISTS RATHER THAN `page={pageData}`
 * ═══════════════════════════════════════════════════════════════════
 * BioPageRenderer draws whatever its `page` prop contains. The public
 * route (`GET /api/bio/{slug}`) returns a document with keys the editor's
 * local `pageData` does not have — `shop_products` being the one that
 * bit. The preview passed `pageData` straight through, so a Shop page
 * previewed as empty and published as full.
 *
 * That is the FOURTH editor/live divergence, and all four were the same
 * shape: the renderer was innocent, the two callers passed different
 * data. So the preview's page object is built HERE, in one place, and
 * `bioPageState.test.js` pins its key set against the public route's
 * response — a fifth instance fails a test instead of reaching a creator.
 *
 * @param pageData      the editor's working draft
 * @param shopProducts  products resolved SERVER-SIDE for the current
 *                      shop config (never filtered in the browser)
 */
export function previewPageFrom(pageData, shopProducts = []) {
  return {
    ...pageData,
    // The renderer reads this for the shop layout. Always present, never
    // undefined, exactly as the public route always includes the key.
    shop_products: shopProducts || [],
  };
}

/** Every `page.*` key BioPageRenderer reads.
 *
 *  The contract both suppliers must satisfy. Kept next to
 *  previewPageFrom so the two are read together. */
export const RENDERER_PAGE_KEYS = [
  "style", "layout_type", "display_name", "username", "category", "bio",
  "profile_image", "social_links", "grid_items", "button_links",
  "embed_blocks", "email_capture", "shop_products", "slug",
];


/** The shop config a shop-layout page ACTUALLY behaves as.
 *
 * ═══════════════════════════════════════════════════════════════════
 *  WHY THIS EXISTS
 * ═══════════════════════════════════════════════════════════════════
 * `pageData.shop` is null until the creator touches a mode button, and
 * three places each invented their own default for that:
 *
 *   the UI     showed "Whole library"   (`shop?.mode || "all"`)
 *   the preview saw `enabled: undefined` and fetched NOTHING
 *   the save    wrote `{enabled: true, mode: "all"}`
 *
 * So a creator who picked a Shop theme and never touched the toggles saw
 * an empty preview and published a page full of products. Same
 * divergence class as the other four, one level further up: the default
 * itself disagreed between callers.
 *
 * One function, three callers. A shop-layout page is enabled by
 * definition — choosing the layout IS the decision to have a shop.
 */
export function effectiveShop(pageData) {
  const shop = pageData?.shop || {};
  return {
    enabled: showsShop(pageData?.layout_type),
    mode: shop.mode || "all",
    product_ids: shop.product_ids || [],
    title: shop.title || "Shop",
    layout: shop.layout || "grid",
  };
}

/* ═══════════════════════════════════════════════════════════════════
 *  LINK ORDER
 * ═══════════════════════════════════════════════════════════════════
 * `BioButtonLink` has carried an `order: int = 0` field since it was
 * written. Nothing read it: `addButtonLink` set it once on create,
 * `removeButtonLink` never renumbered, and the renderer drew
 * `page.button_links` in array order. So `order` was a field that
 * existed, round-tripped, drifted out of step with reality, and would
 * have scrambled a page the day anyone started trusting it.
 *
 * Reordering makes that decision unavoidable, so it is made here:
 * ORDER IS THE TRUTH. The editor renumbers on every mutation and the
 * renderer sorts by it.
 *
 * Legacy pages are safe. Every stored link has `order: 0`, and
 * `Array.prototype.sort` has been stable since ES2019 — an all-zero
 * list comes back in exactly the array order it went in with, which is
 * the order those pages already render in today.
 */

/** Move `from` to `to`, returning a NEW array. Out-of-range indices are
 *  a no-op rather than an error: the callers are drag handlers and
 *  arrow buttons, where "past the end" is a normal gesture. */
export function reorderList(list, from, to) {
  const items = Array.isArray(list) ? [...list] : [];
  if (from === to) return items;
  if (from < 0 || from >= items.length) return items;
  if (to < 0 || to >= items.length) return items;
  const [moved] = items.splice(from, 1);
  items.splice(to, 0, moved);
  return items;
}

/** Rewrite `order` to match array position. Called after EVERY mutation
 *  — add, remove, reorder — so the field can never describe a list that
 *  no longer exists. */
export function withSequentialOrder(list) {
  return (Array.isArray(list) ? list : []).map((item, i) => ({ ...item, order: i }));
}

/** Read order: sort ascending, stably. A missing `order` sorts as 0,
 *  which keeps pre-ordering pages in their array order. */
export function sortByOrder(list) {
  return [...(Array.isArray(list) ? list : [])]
    .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
}
